import type { NodeSelection } from "@tiptap/pm/state";
import { button, element } from "./dom";
import { filePicker, formatLabel, isBoxTag, type PickerContext, stagePicker, TAG_PICKERS, tagPicker } from "./pickers";
import { selectInput } from "./panels";
import { MAP_SIZES, mapParts, monsterArgs, PICTURE_SIZES, parameterize } from "./pieces";

const LINK = "text-link underline hover:no-underline";
const SELECT = "rounded border border-line bg-surface py-0 pl-1.5 pr-7 text-sm";
const IMAGE_IAL = /^\{: class="([\w-]+)"\}$/;

// Where the site shows the record a tag names, by the key of `openPaths`.
const OPENS: Record<string, string> = { MONSTERBOX: "monstro", MONSTRO: "monstro", SPELLBOX: "magia", MAGIA: "magia", EQUIPAMENTO: "equipamento" };

// What a selected piece offers besides Remover: its kind and name, then the controls that fit it. A control that
// changes the piece replaces the one node and keeps the rest of its arguments.
export function atomBar(c: PickerContext, selection: NodeSelection): HTMLElement[] {
	const { node, from: pos } = selection;
	const { t, editor } = c;
	const name = String(node.attrs.tag ?? "");
	const specific = `atom_${name}`;
	const parts: HTMLElement[] = [element("span", "text-ink-muted", t(t(specific) === specific ? `atom_${node.type.name}` : specific))];

	// The piece may have changed or moved while a picker was open; then nothing is replaced.
	const change = (attrs: Record<string, unknown>) => {
		if (!editor.state.doc.nodeAt(pos)?.eq(node)) return;
		editor.view.dispatch(editor.state.tr.setNodeMarkup(pos, undefined, { ...node.attrs, ...attrs }));
		editor.commands.focus();
	};
	const swap = (open: () => void) => button(LINK, t("swap"), open);
	const choose = (current: string, sizes: { cls: string; label: string }[], set: (size: string) => void) => {
		const options = sizes.map((size) => ({ value: size.cls, label: t(size.label) }));
		if (!sizes.some((size) => size.cls === current)) options.push({ value: current, label: current });
		const select = selectInput(options, current, SELECT);
		select.setAttribute("aria-label", t("size_title"));
		select.addEventListener("change", () => set(select.value));
		return select;
	};

	if (node.type.name === "odoBox" && name === "MAPA") {
		const { path, size, title } = mapParts(String(node.attrs.args));
		parts.push(
			choose(size, MAP_SIZES, (next) => change({ args: `${path}, ${next}, ${title}` })),
			swap(() =>
				filePicker(c, "mapa", "panel_map", (ref, close) => {
					change({ args: `${ref.value}, ${size}, ${title}` });
					close();
				}),
			),
		);
	} else if (node.type.name === "odoTag" && name === "ESTAGIO") {
		parts.push(swap(() => stagePicker(c, (level) => change({ args: level }))));
	} else if (node.type.name === "odoImage") {
		const size = IMAGE_IAL.exec(String(node.attrs.ial ?? ""))?.[1];
		if (size) parts.push(choose(size, PICTURE_SIZES, (next) => change({ ial: `{: class="${next}"}` })));
		parts.push(
			swap(() =>
				filePicker(c, "imagem", "panel_picture", (ref, close) => {
					change({ src: ref.value });
					close();
				}),
			),
		);
	} else if (TAG_PICKERS[name]) {
		const args = String(node.attrs.args);
		const card = name === "MONSTERBOX" ? monsterArgs(args) : { slug: args, format: null };
		// The renderer finds a record by the parameterized argument (only a monster box has a format after a comma); "exemplo" is its placeholder monster, which has no page.
		const slug = parameterize(card.slug);
		if (!isBoxTag(name)) parts.push(element("strong", "", args));
		if (name === "MONSTERBOX") {
			const formats = [null, ...c.monsterFormats];
			if (!formats.includes(card.format)) formats.push(card.format);
			const select = selectInput(
				formats.map((format) => ({ value: format ?? "", label: formatLabel(c, format) })),
				card.format ?? "",
				SELECT,
			);
			select.setAttribute("aria-label", t("format_title"));
			select.addEventListener("change", () => change({ args: select.value ? `${card.slug}, ${select.value}` : card.slug }));
			parts.push(select);
		}
		if (OPENS[name] && slug && !(name === "MONSTERBOX" && slug === "exemplo")) {
			const open = element("a", LINK, t("open"));
			open.href = `${c.openPaths[OPENS[name]]}/${encodeURIComponent(slug)}`;
			open.target = "_blank";
			open.rel = "noopener";
			parts.push(open);
		}
		parts.push(swap(() => tagPicker(c, name, (next) => change({ args: next }), card.format)));
	}
	return parts;
}
